import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatDateOnly } from "@alm/domain";
import type { RouterOutputs } from "@alm/api-contracts";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { DocumentStatusBadge, documentStatusLabel } from "./-components/document-status-badge";
import { EmployeeFileLink } from "./-components/employee-file-link";

export const Route = createFileRoute("/_authenticated/_workforce/hr/missing-documents")({
  component: MissingDocuments,
});

type MissingRow = RouterOutputs["workforce"]["employeeDocuments"]["missingReport"][number];

const COLUMNS: ReadonlyArray<ReportColumn<MissingRow>> = [
  {
    header: "Employee",
    cell: (row) => (
      <PersonCell
        name={row.name || "Unnamed"}
        renderLink={(name) => (
          <EmployeeFileLink userId={row.userId} name={row.name}>
            {name}
          </EmployeeFileLink>
        )}
      />
    ),
    value: (row) => row.name || "Unnamed",
  },
  { header: "Email", cell: (row) => row.email ?? "-" },
  { header: "Document", cell: (row) => row.requirement },
  {
    header: "Status",
    // Rejected or waiting on review is owed too, but the next step differs:
    // chase the employee, or review what they already handed in.
    cell: (row) => <DocumentStatusBadge status={row.status} />,
    value: (row) => documentStatusLabel(row.status),
  },
  {
    header: "Expired on",
    // Only for a lapsed one: a file waiting on review carries the date it will
    // lapse, which is not a date it expired on.
    cell: (row) =>
      row.status === "expired" && row.expiresAt ? formatDateOnly(row.expiresAt) : "-",
    value: (row) => (row.status === "expired" ? row.expiresAt : null),
  },
];

/** Who still owes a document they are required to hold, and which one. A document that has passed
 * its expiry counts as owed, because at survey an expired licence is not a licence. An empty report
 * means nothing is outstanding; a failed one says so rather than reading as compliance. */
function MissingDocuments() {
  const { facilityId } = useFacility();
  const report = useQuery(
    orpc.workforce.employeeDocuments.missingReport.queryOptions({ input: { facilityId } }),
  );

  const rows = report.data ?? [];
  const employees = new Set(rows.map((row) => row.userId)).size;
  const expired = rows.filter((row) => row.status === "expired").length;

  const meta = report.isError
    ? "Report unavailable"
    : report.isPending
      ? "Building the report…"
      : rows.length > 0
        ? `${employees} ${employees === 1 ? "employee" : "employees"} owe ${rows.length} ${rows.length === 1 ? "document" : "documents"}`
        : "Employees who still owe a required document";

  return (
    <>
      <PageHeader title="Missing documents" meta={meta} />

      {report.data ? (
        <StatTiles>
          <StatTile label="Employees" value={employees} hint="Owe at least one document" />
          <StatTile label="Documents" value={rows.length} hint="Missing or expired" />
          <StatTile
            label="Expired"
            value={expired}
            hint="Held once, now lapsed"
            tone={expired > 0 ? "crit" : undefined}
          />
        </StatTiles>
      ) : null}

      {report.isError ? (
        <ReadFailed
          message="Could not load the missing-document report. This is not a statement that everybody is compliant."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : (
        <ReportTable
          rows={report.data}
          pending={report.isPending}
          filename="missing-documents.csv"
          rowKey={(row) => row.requirementId}
          empty={{
            title: "Everyone is up to date",
            description: "No required document is missing or expired.",
          }}
          columns={COLUMNS}
        />
      )}
    </>
  );
}
